import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import {
  useCustomToast,
  useSearch,
  type OrderOptions,
  type SearchFilter,
  type SearchParams,
  type Pagination,
} from '@/shared/lib';
import { DEFAULT_ORDER_OPTION } from '@/shared/config';
import {
  reminderHistoryApi,
  useReminderHistoryStore,
  type NoticeListItem,
  type NoticeRecipientStatus,
  type NoticeRecipientsResponse,
} from '@/entities/ReminderHistory';
import {
  DEFAULT_REMINDER_HISTORY_SORT_OPTION,
  GENERAL_HISTORY_SORT_OPTIONS,
  MY_HISTORY_SORT_OPTIONS,
} from './sort-options';
import {
  hasActiveReminderHistoryFilters,
  mapReminderHistoryDate,
  normalizeReminderHistoryFilters,
} from './reminder-history-filters';
import { useReminderHistoryPermissions } from './useReminderHistoryPermissions';
import type {
  ReminderGeneralHistoryFilters,
  ReminderHistoryBaseFilters,
  ReminderHistoryFilters,
  NoticeSortField,
  NoticeSortOrder,
  ReminderTab,
} from './types';

function mapApiMetadata(meta?: {
  total: number;
  page: number;
  totalPages: number;
  itemsPerPage: number;
}): Pagination {
  const page = meta?.page ?? 1;
  const totalPages = meta?.totalPages ?? 1;
  return {
    total: meta?.total ?? 0,
    page,
    totalPages,
    isNextPage: page < totalPages,
    isPreviousPage: page > 1,
    itemsPerPage: meta?.itemsPerPage,
  };
}

const MY_HISTORY_TAB: ReminderTab = 'my-history';
const GENERAL_HISTORY_TAB: ReminderTab = 'general-history';

const API_BATCH_SIZES = ['10', '25', '30', '50'] as const;
type ApiBatchSize = (typeof API_BATCH_SIZES)[number];
type ReminderHistoryItemsPerPage = ApiBatchSize | 'all';

const isOneOf = <T extends readonly string[]>(value: string, options: T): value is T[number] =>
  options.some((option) => option === value);

function isApiBatchSize(value: string): value is ApiBatchSize {
  return isOneOf(value, API_BATCH_SIZES);
}

function resolveApiBatchSize(amount: string | number): ApiBatchSize {
  const normalizedAmount = String(amount);
  return isApiBatchSize(normalizedAmount) ? normalizedAmount : '30';
}

function isLegacyShowAllAmount(amount: unknown): boolean {
  const normalizedAmount = Array.isArray(amount) ? amount[0] : amount;
  return normalizedAmount === '100' || normalizedAmount === 100;
}

export function useReminderHistoryList(initialTab: ReminderTab = MY_HISTORY_TAB) {
  const toast = useCustomToast();
  const route = useRoute();
  const { canReadGeneralHistory } = useReminderHistoryPermissions();
  const reminderHistoryStore = useReminderHistoryStore();
  const { processingNoticeId, acknowledgeVersion } = storeToRefs(reminderHistoryStore);
  const activeTab = ref<ReminderTab>(initialTab);
  const selectedSortId = ref(DEFAULT_REMINDER_HISTORY_SORT_OPTION.id);
  const currentSort = ref<NoticeSortField | ''>('');
  const currentOrder = ref<NoticeSortOrder | ''>('');
  const currentFilters = ref<ReminderHistoryFilters>({});
  const expandedNoticeId = ref<number | null>(null);
  const isRecipientsLoading = ref(false);
  const recipientErrorMessage = ref<string | null>(null);
  const recipients = ref<NoticeRecipientStatus[]>([]);
  const recipientsCache = new Map<number, NoticeRecipientsResponse>();
  const skipNextAcknowledgeRefresh = ref(false);

  const sortOptions = computed(() =>
    activeTab.value === MY_HISTORY_TAB ? MY_HISTORY_SORT_OPTIONS : GENERAL_HISTORY_SORT_OPTIONS
  );

  const orderOptionsForTable = computed<OrderOptions[]>(() =>
    sortOptions.value.map((option) => ({
      label: option.name,
      sort: option.sort,
      order: option.order,
    }))
  );

  const selectedSort = computed(
    () =>
      sortOptions.value.find((option) => option.id === selectedSortId.value) ??
      sortOptions.value[0] ??
      DEFAULT_REMINDER_HISTORY_SORT_OPTION
  );

  const searchFunction = async (
    params: SearchParams
  ): Promise<{ data: NoticeListItem[]; metadata: Pagination }> => {
    const batchSize = resolveApiBatchSize(params.amount);
    const isShowAllMode = activeTab.value !== GENERAL_HISTORY_TAB && search.isShowAllMode.value;
    const itemsPerPage: ReminderHistoryItemsPerPage = isShowAllMode ? 'all' : batchSize;

    const commonQuery = {
      page: params.page,
      itemsPerPage,
      term: params.search ?? undefined,
      sort: selectedSort.value.sort,
      order: selectedSort.value.order,
    };

    const baseFilters: ReminderHistoryBaseFilters = {
      createdAt: currentFilters.value.createdAt,
      createdBy: currentFilters.value.createdBy,
      category: currentFilters.value.category,
    };

    const query =
      activeTab.value === MY_HISTORY_TAB
        ? {
            ...commonQuery,
            ...baseFilters,
          }
        : {
            ...commonQuery,
            ...baseFilters,
            recipientId: (currentFilters.value as ReminderGeneralHistoryFilters).recipientId,
          };

    const response =
      activeTab.value === MY_HISTORY_TAB
        ? await reminderHistoryApi.getMyHistory(query)
        : await reminderHistoryApi.getGeneralHistory(query);

    return {
      data: (response.data ?? []) as NoticeListItem[],
      metadata: mapApiMetadata(response.metadata),
    };
  };

  const search = useSearch<NoticeListItem>({
    search: '',
    sort: { sort: DEFAULT_ORDER_OPTION.sort, order: DEFAULT_ORDER_OPTION.order },
    filter: {},
    searchUrl: '/notices',
    searchFunction,
    skipShowAllPrefetch: true,
  });

  const notices = search.items;
  const isLoading = search.loading;
  const currentPage = search.currentPage;
  const itemsPerPage = search.selectedQuantity;
  const totalItems = computed(() => search.metadata.value?.total ?? 0);
  const totalPages = computed(() => search.metadata.value?.totalPages ?? 1);
  const displayedNotices = computed(() =>
    search.isInfiniteScrollMode.value ? search.allItems.value : notices.value
  );

  const assignRecipients = (payload: NoticeRecipientsResponse) => {
    recipients.value = payload.recipients;
  };

  const loadRecipients = async (noticeId: number, useCache = true) => {
    recipientErrorMessage.value = null;

    const cached = useCache ? recipientsCache.get(noticeId) : null;
    if (cached) {
      assignRecipients(cached);
      return;
    }

    recipients.value = [];
    isRecipientsLoading.value = true;

    try {
      const payload = await reminderHistoryApi.getNoticeRecipients(noticeId);
      recipientsCache.set(noticeId, payload);
      assignRecipients(payload);
    } catch {
      recipientErrorMessage.value = 'Não foi possível carregar os destinatários deste aviso.';
    } finally {
      isRecipientsLoading.value = false;
    }
  };

  const syncExpandedRecipients = async () => {
    const expandedId = expandedNoticeId.value;
    if (expandedId === null || activeTab.value !== GENERAL_HISTORY_TAB) {
      return;
    }

    const hasExpandedNotice = displayedNotices.value.some((notice) => notice.id === expandedId);
    if (!hasExpandedNotice) {
      expandedNoticeId.value = null;
      recipients.value = [];
      recipientErrorMessage.value = null;
      return;
    }

    await loadRecipients(expandedId, false);
  };

  const reloadWithCacheClear = async () => {
    recipientsCache.clear();
    await search.initSearch();
    await syncExpandedRecipients();
  };

  const tabs = computed(() => {
    const options = [{ id: MY_HISTORY_TAB, text: 'Meu Histórico' }];

    if (canReadGeneralHistory.value) {
      options.push({ id: GENERAL_HISTORY_TAB, text: 'Histórico Geral' });
    }

    return options;
  });

  const mapDate = (value: string | null | undefined) => mapReminderHistoryDate(value);

  const handleSearch = async (term: string) => {
    recipientsCache.clear();
    await search.handleSearch(term);
  };

  const handlePageChange = async (page: number) => {
    recipientsCache.clear();
    await search.onPageChanged(page);
  };

  const handleSortUpdate = async (sort: NoticeSortField | '', order: NoticeSortOrder | '') => {
    if (!sort && !order) {
      currentSort.value = '';
      currentOrder.value = '';
      selectedSortId.value = DEFAULT_REMINDER_HISTORY_SORT_OPTION.id;
      await reloadWithCacheClear();
      return;
    }

    currentSort.value = sort;
    currentOrder.value = order;
    const match = sortOptions.value.find((o) => o.sort === sort && o.order === order);
    selectedSortId.value =
      match?.id ?? sortOptions.value[0]?.id ?? DEFAULT_REMINDER_HISTORY_SORT_OPTION.id;

    await reloadWithCacheClear();
  };

  const handleItemsPerPageChange = async (value: string | number) => {
    const nextValue = Number(value);
    if (nextValue !== 10 && nextValue !== 25 && nextValue !== 50 && nextValue !== 100) {
      return;
    }

    recipientsCache.clear();
    await search.handleQuantityChange(nextValue);
  };

  const handleFilterUpdate = async (filters: ReminderHistoryFilters) => {
    const normalizedFilters = normalizeReminderHistoryFilters(filters);

    currentFilters.value =
      activeTab.value === GENERAL_HISTORY_TAB
        ? normalizedFilters
        : {
            createdAt: normalizedFilters.createdAt,
            createdBy: normalizedFilters.createdBy,
            category: normalizedFilters.category,
          };

    recipientsCache.clear();
    await search.handleFilter(currentFilters.value as SearchFilter);
  };

  const handleAcknowledge = async (noticeId: number) => {
    if (processingNoticeId.value !== null) {
      return;
    }

    skipNextAcknowledgeRefresh.value = true;

    try {
      const didAcknowledge = await reminderHistoryStore.acknowledgeNotice(noticeId);

      if (!didAcknowledge) {
        return;
      }

      await reloadWithCacheClear();
    } catch {
      toast.showErrorToast({
        title: 'Erro ao confirmar ciência do aviso',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      skipNextAcknowledgeRefresh.value = false;
    }
  };

  const reloadWithPageFallback = async (deletedCount: number) => {
    recipientsCache.clear();
    const onPage = notices.value.length;
    const isCurrentPageEmpty = onPage - deletedCount <= 0;

    if (isCurrentPageEmpty && currentPage.value > 1) {
      await search.onPageChanged(currentPage.value - 1);
    } else {
      await search.initSearch();
    }
  };

  const toggleRecipientsRow = async (noticeId: number) => {
    if (expandedNoticeId.value === noticeId) {
      expandedNoticeId.value = null;
      recipients.value = [];
      recipientErrorMessage.value = null;
      return;
    }

    expandedNoticeId.value = noticeId;
    await loadRecipients(noticeId);
  };

  watch(acknowledgeVersion, async (currentVersion, previousVersion) => {
    if (currentVersion === previousVersion) {
      return;
    }

    if (skipNextAcknowledgeRefresh.value) {
      skipNextAcknowledgeRefresh.value = false;
      return;
    }

    await reloadWithCacheClear();
  });

  onMounted(async () => {
    if (activeTab.value === GENERAL_HISTORY_TAB && !canReadGeneralHistory.value) {
      activeTab.value = MY_HISTORY_TAB;
    }

    if (isLegacyShowAllAmount(route.query.amount)) {
      search.selectedQuantity.value = 100;
    }

    await search.initSearch();
  });

  return {
    activeTab,
    tabs,
    notices,
    displayedNotices,
    allNotices: search.allItems,
    loadingMore: search.loadingMore,
    hasMoreNotices: search.hasMoreItems,
    isInfiniteScrollMode: search.isInfiniteScrollMode,
    searchTerm: search.searchTerm,
    isLoading,
    currentPage,
    totalItems,
    totalPages,
    itemsPerPage,
    sortOptions,
    orderOptionsForTable,
    selectedSort,
    selectedSortId,
    sort: computed(() => selectedSort.value.sort),
    order: computed(() => selectedSort.value.order),
    currentSort,
    currentOrder,
    currentFilters,
    isFilterActive: computed(() => hasActiveReminderHistoryFilters(currentFilters.value)),
    mapDate,
    expandedNoticeId,
    isRecipientsLoading,
    recipientErrorMessage,
    recipients,
    processingNoticeId,
    handleSearch,
    handlePageChange,
    handleSortUpdate,
    handleItemsPerPageChange,
    handleFilterUpdate,
    handleAcknowledge,
    reloadAfterBulkDelete: reloadWithPageFallback,
    toggleRecipientsRow,
    loadNotices: reloadWithCacheClear,
    bulkDeleteApi: (ids: number[]) => reminderHistoryApi.bulkDeleteFromGeneralHistory(ids),
    canReadGeneralHistory,
  };
}
